import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, RingTab, TagPill, Face, EmptyBlock, PINNED, MUTED, RULE, CHIP } from './clubUi';
import LoadFailed from './LoadFailed';
import { ago, dateLabel, plural } from '../../utils/surfaceFormat';

/**
 * FORUM (README §1): the FORUM panel — category chips (rainbow active) + `START A THREAD` /
 * `OPEN THE FORUM →`, admin `EDIT CATEGORIES →` — then the thread rows (44px avatar, title,
 * PINNED / category pill, `by · N REPLIES · LAST 2H`, OPEN → thread).
 */
export function threadMeta(t, now = new Date()) {
  const parts = [t.authorName || 'Member', plural(t.replyCount || 0, 'REPLY', 'REPLIES')];
  if (t.lastActivityAt) parts.push(`LAST ${ago(t.lastActivityAt, now)}`);
  else if (t.createdAt) parts.push(dateLabel(t.createdAt));
  return parts.join(' · ');
}

export default function ClubForumTab({ club, state, forum, loading, categoryId, onCategory, mobile = false, loadError = false, onRetryLoad }) {
  const navigate = useNavigate();
  const threads = forum?.threads || [];
  const cats = forum?.categories || [];
  const total = forum?.totalThreadCount ?? threads.length;
  const active = cats.find((c) => c.id === categoryId);
  const forumUrl = `/clubs/${club.id}/forum`;
  const isMember = !!state?.isMember;
  const chip = (key, label, n, on, onClick) => <RingTab key={key} on={on} off={CHIP} height={36} fontSize={10} tracking=".12em" aria-pressed={on} onClick={onClick} data-testid={`forum-cat-${key}`}>{label}{n != null && <span style={{ opacity: 0.6 }}>{n}</span>}</RingTab>;
  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="club-forum">
      <ClubPanel phone={mobile}>
        <PanelHead title="FORUM">
          {state?.isAdmin && <button type="button" onClick={() => navigate(`/clubs/${club.id}/manage?tab=features#forum-categories`)} className="font-mono uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} data-testid="edit-categories">EDIT CATEGORIES →</button>}
        </PanelHead>
        {isMember && (
          <span className="flex flex-wrap" style={{ gap: 6 }}>
            <PillButton size={36} fontSize={10} onClick={() => navigate(`${forumUrl}#new`)} data-testid="new-thread">START A THREAD</PillButton>
            <PillButton variant="tertiary" size={36} fontSize={10} onClick={() => navigate(`${forumUrl}${categoryId ? `?category=${categoryId}` : ''}`)} data-testid="open-forum">OPEN THE FORUM →</PillButton>
          </span>
        )}
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          {chip('all', 'ALL', total, !categoryId, () => onCategory(null))}
          {cats.map((c) => chip(c.id, c.name, c.threadCount, categoryId === c.id, () => onCategory(c.id)))}
        </span>
      </ClubPanel>
      <ClubPanel phone={mobile}>
        <PanelHead title={active ? active.name.toUpperCase() : 'LATEST THREADS'} right={`${threads.length} ${threads.length === 1 ? 'THREAD' : 'THREADS'}`} />
        {loading && !forum ? (
          <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>LOADING…</span>
        ) : loadError ? (
          <LoadFailed what="the forum" onRetry={onRetryLoad} testId="forum-failed" />
        ) : threads.length === 0 ? (
          <EmptyBlock title="NOTHING HERE YET" body="Start the first thread." action={isMember ? 'NEW THREAD' : null} onAction={() => navigate(`${forumUrl}#new`)} />
        ) : (
          <div className="flex flex-col">
            {threads.map((t, i) => (
              <div key={t.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid={`thread-${t.id}`}>
                <Face src={t.authorAvatarUrl} name={t.authorName} size={44} tintKey={t.authorUserId} />
                <button type="button" onClick={() => navigate(`/clubs/${club.id}/forum/${t.id}`)} className="flex min-w-0 flex-col text-left font-mono" style={{ flex: '1 1 180px', gap: 4, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}>
                  <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{t.title}</span>
                    {t.isPinned ? <TagPill bg={PINNED} ring="#000">PINNED</TagPill> : t.categoryName && <TagPill>{t.categoryName}</TagPill>}
                  </span>
                  <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{threadMeta(t)}</span>
                </button>
                <PillButton variant="tertiary" size={34} fontSize={9} onClick={() => navigate(`/clubs/${club.id}/forum/${t.id}`)}>OPEN</PillButton>
              </div>
            ))}
          </div>
        )}
      </ClubPanel>
    </div>
  );
}
